import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useGroupStore } from './group';
import api from '@/api';
import Common from '@/libs/common';
import {
  StopType,
  type DeviationStop,
  type FormatedUpdate,
  type StopTimeWithType,
  type TripUpdate,
} from '@/@types/trip-updates';
import { useAppStore } from './app';
import { isMockTagsEnabled, mockTripUpdates, onMockTagsReady } from '@/libs/dev/mock-tags';
import { LocationType, type Stop, type StopTime, type StopTimeData } from '@/@types/gtfs-ressource';
import { useI18n } from 'vue-i18n';
import { isAppVisible } from '@/libs/app-visibility';
import { tripUpdateOnServiceDate } from '@/libs/journey/realtime';
import { isInAfterMidnightWindow, previousGtfsDate } from '@pysae/transit-core/service-day';

export const useTripUpdatesStore = defineStore('tripUpdates', () => {
  const groupStore = useGroupStore();
  const appStore = useAppStore();
  const { t } = useI18n();

  // #region trip-update global polling & storage
  const TRIP_UPDATE_REFRESH_INTERVAL = 60000;
  const TRIP_UPDATE_REFRESH_IF_ERROR_INTERVAL = 10000;

  const tripUpdates = ref<Array<TripUpdate>>(Common.safeLSParse('tripUpdates'));
  let tripUpdatesTimeout: ReturnType<typeof setTimeout> | undefined;
  let isTripUpdatesPolling = false;
  // Bumped on every (re)start, stop and visibility change: a loop whose call
  // was in flight at that moment neither stores its result nor reschedules,
  // so only one loop ever runs.
  let tripUpdatesRun = 0;

  async function refreshTripUpdates(run: number) {
    let delay = TRIP_UPDATE_REFRESH_INTERVAL;
    try {
      const updates = await fetchTripUpdates();
      if (run !== tripUpdatesRun) return;
      if (updates) setTripUpdates(updates);
    } catch (e) {
      if (run !== tripUpdatesRun) return;
      console.warn('Error while fetching trip updates, retrying in 10s:', e);
      delay = TRIP_UPDATE_REFRESH_IF_ERROR_INTERVAL;
    }
    tripUpdatesTimeout = setTimeout(() => refreshTripUpdates(run), delay);
  }

  function restartTripUpdatesPolling() {
    clearTimeout(tripUpdatesTimeout);
    tripUpdatesTimeout = undefined;
    const run = ++tripUpdatesRun;
    if (isTripUpdatesPolling && isAppVisible.value) refreshTripUpdates(run);
  }

  /**
   * Poll trip updates of the current group
   */
  function startTripUpdatesPolling() {
    isTripUpdatesPolling = true;
    restartTripUpdatesPolling();
  }

  function stopTripUpdatesPolling() {
    isTripUpdatesPolling = false;
    restartTripUpdatesPolling();
  }

  watch(isAppVisible, restartTripUpdatesPolling);

  /**
   * Check updates for current trip
   */
  async function loadTripUpdates() {
    const updates = await fetchTripUpdates();
    if (updates) setTripUpdates(updates);
  }

  async function fetchTripUpdates(): Promise<TripUpdate[] | undefined> {
    const date = appStore.todayDateGtfs;
    const groupId = groupStore.group?.group_id;
    const gtfsId = groupStore.group?.current_file;
    if (!groupId || !gtfsId) return;

    const mocking = import.meta.env.DEV && isMockTagsEnabled();
    // #293: after midnight, the night trips of the day before still run; their updates are
    // stored on that day, the route takes one date only.
    const tz = groupStore.group?.tz || 'Europe/Paris';
    const dates = isInAfterMidnightWindow(date, Date.now() / 1000, tz)
      ? [date, previousGtfsDate(date)]
      : [date];
    const tripUpdates = await Promise.all(dates.map(d => api.getTripUpdates(d, groupId, gtfsId)))
      .then(results => ([] as TripUpdate[]).concat(...results))
      .catch(e => {
        // Mock mode still has to tag the trips when the real feed is unreachable
        if (!mocking) throw e;
        return [];
      });
    // Mocks first: the lookup takes the first match, and the scenario must win
    return mocking ? [...mockTripUpdates(date, groupId, gtfsId), ...tripUpdates] : tripUpdates;
  }

  // The first poll runs before the GTFS is in, and the mocks are built from it
  if (import.meta.env.DEV && isMockTagsEnabled()) onMockTagsReady(() => loadTripUpdates());

  function setTripUpdates(tripUpdatesToSet: Array<TripUpdate>) {
    tripUpdates.value = tripUpdatesToSet;
    localStorage.setItem('tripUpdates', JSON.stringify(tripUpdatesToSet));
  }

  /**
   * Find the trip update of a trip occurrence. After midnight the updates of two service
   * days are loaded, with the same trip_id: `serviceDate` picks the one of the occurrence.
   */
  function getTripUpdateByTripId(tripId: string, serviceDate?: string): TripUpdate | undefined {
    return tripUpdates.value?.find(
      tripUpdate => tripUpdate.trip_id === tripId && tripUpdateOnServiceDate(tripUpdate, serviceDate),
    );
  }
  // #endregion

  // #region trip-update formatted for display in trip detail page and map when viewing a single trip context

  /**
   * current trip Update formatted when viewing a trip detail page
   */
  const currentTripUpdateFormatted = ref<FormatedUpdate>();

  const allGtfsStops = ref<{ [key: string]: Stop }>({});
  const defaultTripStops = ref<Map<string, Stop>>(new Map());
  const reusedStops = ref<Map<string, Stop>>(new Map());
  const createdStops = ref<Map<string, Stop>>(new Map());
  const overridedStops = ref<Map<string, Stop>>(new Map());
  /** Stop ids of both ends of the current trip, in journey order: the map pins and labels them. */
  const currentTripTerminusIds = ref<Array<string>>([]);

  /**
   * Updated stop list for map
   */
  const getTripUpdateStops = computed<Map<string, Stop>>(() => {
    return new Map([
      ...defaultTripStops.value,
      ...reusedStops.value,
      ...createdStops.value,
      ...overridedStops.value,
    ]);
  });

  /**
   * Updated shape for map
   */
  const getUpdatedShape = computed<Array<[number, number]> | undefined>(() => {
    return currentTripUpdateFormatted.value?.shape;
  });

  /**
   * convert trip updates to a FeedStopTimes list that can be used & displayed for front-end purpose
   * Also create new specific stops
   */
  function initFeedStopTimesAndStops(
    stops: { [stopId: string]: Stop },
    gtfsStopTimes?: Array<StopTime>,
    tripUpdates?: TripUpdate,
  ): StopTimeWithType[] | undefined {
    if (gtfsStopTimes) {
      $resetStops();
      allGtfsStops.value = stops;

      const fstList: Array<StopTimeWithType> = [];
      // Create default list & set regular/default type
      gtfsStopTimes.forEach(stopTime => {
        const type =
          tripUpdates && tripUpdates.skipped_stop_time_seqs?.includes(stopTime.stop_sequence)
            ? StopType.CANCELED
            : StopType.REGULAR;

        // set default trip stops
        defaultTripStops.value.set(stopTime.stop_id, { ...stops[stopTime.stop_id], type: type });

        const name = (stops[stopTime.stop_id] || {}).stop_name || stopTime.stop_id;
        const feedStopTime = { ...stopTime, ...{ type: type, name: name } };
        fstList.push(feedStopTime);
      });

      // Handle temporary_stops
      if (tripUpdates) {
        tripUpdates.temporary_stops?.forEach(temporaryStop => {
          let toInsert: StopTimeWithType = {} as StopTimeWithType;
          switch (temporaryStop.type) {
            case StopType.SHIFT: {
              const feedIndex = fstList.findIndex(fst => fst.stop_sequence === temporaryStop.stop_sequence);
              // A SHIFT matching no sequence of the trip is inconsistent data: ignore it rather than break the whole list
              if (feedIndex === -1) break;
              // replace directly in feedStopTimes since it's an update
              fstList[feedIndex] = {
                ...fstList[feedIndex],
                ...convertDeviationToFst(temporaryStop),
              };
              // updated stop :
              addOverridedStop(fstList[feedIndex].stop_id, temporaryStop.stop_lat, temporaryStop.stop_lon);
              break;
            }
            case StopType.REUSE: {
              toInsert = convertDeviationToFst(temporaryStop);
              addReusedStop(temporaryStop.stop_id);
              fstList.push(toInsert);
              break;
            }
            case StopType.AD_HOC:
              {
                toInsert = convertDeviationToFst(temporaryStop);

                addCreatedStop(
                  temporaryStop.stop_id,
                  temporaryStop.stop_name,
                  temporaryStop.stop_lat,
                  temporaryStop.stop_lon,
                );
                fstList.push(toInsert);
              }
              break;
            default:
              break;
          }
        });
      }
      fstList.sort((a, b) => a.stop_sequence - b.stop_sequence);
      currentTripTerminusIds.value =
        fstList.length > 0 ? [fstList[0].stop_id, fstList[fstList.length - 1].stop_id] : [];
      return fstList;
    }
  }

  function addCreatedStop(stopId: string, name: string, lat: number, long: number) {
    const stop: Stop = {
      stop_id: stopId,
      stop_name: name,
      stop_lat: lat,
      stop_lon: long,
      location_type: LocationType.STOP,
      parent_station: '',
    };
    stop.type = StopType.AD_HOC;
    createdStops.value.set(stopId, stop);
  }

  function addReusedStop(stopId: string) {
    const stop = JSON.parse(JSON.stringify(allGtfsStops.value?.[stopId]));
    if (stop) {
      stop.type = StopType.REUSE;
      reusedStops.value.set(stopId, stop);
    }
  }

  function addOverridedStop(stopId: string, lat: number, long: number) {
    const defaultStop = JSON.parse(JSON.stringify(defaultTripStops.value.get(stopId)));
    if (defaultStop) {
      defaultStop.stop_lat = lat;
      defaultStop.stop_lon = long;
      defaultStop.type = StopType.SHIFT;
      overridedStops.value.set(stopId, defaultStop);
    }
  }

  function convertDeviationToFst(deviation: DeviationStop): StopTimeWithType {
    return {
      type: deviation.type,
      name: deviation.stop_name,
      arrival_time: deviation.arrival_time,
      departure_time: deviation.departure_time,
      stop_sequence: deviation.stop_sequence,
      stop_id: deviation.stop_id,
    };
  }

  function setCurrentTripUpdateFormatted(tripUpdateFormatted: FormatedUpdate | undefined) {
    currentTripUpdateFormatted.value = tripUpdateFormatted;
  }

  /**
   * Reset trip update single trip detail context
   */
  function $resetCurrentTripUpdateFormatted() {
    currentTripUpdateFormatted.value = undefined;
    $resetStops();
  }

  function $resetStops() {
    allGtfsStops.value = {};
    defaultTripStops.value = new Map();
    reusedStops.value = new Map();
    createdStops.value = new Map();
    overridedStops.value = new Map();
    currentTripTerminusIds.value = [];
  }

  /**
   * Treat & format tripUpdate in order to be used in components
   */
  function formatTripUpdate(
    tripUpdate: TripUpdate,
    isRealTime: boolean,
    departTime: number = 0,
    specificStopTime?: StopTimeData,
  ): FormatedUpdate {
    const now = Date.now() / 1000;
    const updates: FormatedUpdate = {};
    if (tripUpdate.is_canceled) {
      updates.canceledTrip = true;
      return updates;
    }
    if (!isRealTime) {
      let delayStopTime = undefined;
      if (specificStopTime && specificStopTime.stop_sequence && tripUpdate.delays)
        delayStopTime = tripUpdate.delays.find(
          d => d.stop_sequence === specificStopTime.stop_sequence,
        )?.delay;
      if ((delayStopTime && delayStopTime !== 0) || (tripUpdate.delay && tripUpdate.delay !== 0)) {
        const tripUpdateDelay = (delayStopTime ?? 0) + (tripUpdate.delay ?? 0);
        const contentTime =
          departTime < 3600
            ? Common.formatSec(departTime + tripUpdateDelay, true)
            : `${t('common.at')} ${Common.timestampFormatHHMM(departTime + now + tripUpdateDelay)}`;
        updates.delayedTrip = {
          content: tripUpdateDelay,
          contentTime,
          contentDelayTime: Common.formatSec(tripUpdateDelay, true),
          isNegativeDelay: tripUpdateDelay < 0,
        };
      }
    }

    if (tripUpdate.shape) updates.shape = tripUpdate.shape;

    if (tripUpdate.skipped_stop_time_seqs?.length > 0) {
      updates.unservedStops = {
        content: tripUpdate.skipped_stop_time_seqs.length,
        contentDetail: tripUpdate.skipped_stop_time_seqs,
      };
    }
    if (tripUpdate.temporary_stops?.length > 0) {
      const temporaryStops = tripUpdate.temporary_stops.filter(stop =>
        [StopType.AD_HOC, StopType.REUSE].includes(stop.type),
      );
      if (temporaryStops.length > 0) {
        updates.temporaryStops = {
          content: temporaryStops.length,
          contentDetail: temporaryStops,
        };
      }
      const displacedStops = tripUpdate.temporary_stops.filter(stop => stop.type === StopType.SHIFT);
      if (displacedStops.length > 0) {
        updates.displacedStops = {
          content: displacedStops.length,
          contentDetail: displacedStops,
        };
      }
    }
    return updates;
  }

  // #endregion

  return {
    tripUpdates,
    startTripUpdatesPolling,
    stopTripUpdatesPolling,
    getTripUpdateByTripId,
    setCurrentTripUpdateFormatted,
    initFeedStopTimesAndStops,
    formatTripUpdate,
    $resetCurrentTripUpdateFormatted,
    getUpdatedShape,
    getTripUpdateStops,
    currentTripTerminusIds,
  };
});
